import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { BadgeComponent } from '../../../../../shared/components/badge/badge.component';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { ButtonType } from '../../../../../shared/components/primary-button/ButtonType';
import { CourseInstanceDTO } from '../../models/CourseDTO';
import { TranslocoModule } from '@jsverse/transloco';
import { DoughnutChartComponent } from '../../../../../shared/components/doughnut-chart/doughnut-chart.component';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';
import { catchError, map, of, Subject, takeUntil } from 'rxjs';
import { LearningPlanInstanceDetailUiModel } from '../../../learning-plan/models/LearningPlanInstanceDetailUIModel';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { LearningPlanDataService } from '../../../learning-plan/services/learning-plan-data.service';
import { LearningPlanService } from '../../../learning-plan/services/learning-plan.service';
import { toLearningPlanInstanceDetailUiModel } from '../../../learning-plan/mappers/dto-mappers';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { LearningPlanStatusComponent } from '../../../learning-plan/components/learning-plan-status/learning-plan-status.component';
import { LearningPlanStatus } from '../../../learning-plan/models/LearningPlanDTO';
import { TranslocoWithFallbackPipe } from '../../../../../core/pipe/transloco-with-fallback.pipe';

import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';

@Component({
  selector: 'app-course-instance-info-card',
  imports: [
    BadgeComponent,
    PrimaryButtonComponent,
    TranslocoModule,
    MatIconModule,
    DoughnutChartComponent,
    LearningPlanStatusComponent,
    TranslocoWithFallbackPipe,
    LoadingComponent,
  ],
  templateUrl: './course-instance-info-card.component.html',
  styleUrl: './course-instance-info-card.component.css',
})
export class CourseInstanceInfoCardComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly learningPlanDataService = inject(LearningPlanDataService);
  private readonly learningPlanService = inject(LearningPlanService);
  private readonly snackbarService = inject(SnackbarService);

  @Input({ required: true }) courseInstance: CourseInstanceDTO;
  @Input() isFirstCard = false;

  private readonly destroy$: Subject<void> = new Subject();
  protected readonly buttonType = ButtonType;

  learningPlanInstanceDetailsStatus: LearningPlanStatus;
  learningPlanInstanceDetails: LearningPlanInstanceDetailUiModel | null = null;
  loading = true;

  completionPercentage = 0;
  enrolledStudentsCount = 0;
  enrolledStudentsDisplay = '0';
  enrollmentBadgeType: 'transparent' | 'warning' = 'warning';

  ngOnInit(): void {
    this.learningPlanDataService.learningPlanInstanceId.set(
      this.courseInstance.phase.learningPlanInstanceId,
    );
    this.loadLearningPlanDetails();
  }

  private loadLearningPlanDetails(): void {
    this.learningPlanService
      .getLearningPlanInstanceDetails(
        this.courseInstance.phase.learningPlanInstanceId,
      )
      .pipe(
        map((res) => {
          this.learningPlanDataService.learningPlanName.set(
            res.learningPlanInstance.name,
          );
          this.learningPlanInstanceDetailsStatus =
            res.learningPlanInstance.status;
          return toLearningPlanInstanceDetailUiModel(res.learningPlanInstance);
        }),
        catchError((error: CustomPortalError) => {
          this.snackbarService.error(error.errorType);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          this.learningPlanInstanceDetails = result;
          this.updateComputedProperties();
          this.loading = false;
        },
      });
  }

  goToCourseDetails(id: string): void {
    this.analyticsService.track('course_setup_clicked');
    void this.router.navigate(['/portal/courses', id]);
  }

  goToLearningPlanDetails(id: string): void {
    this.analyticsService.track('learning_plan_setup_clicked');
    void this.router.navigate(['/portal/learningPlans', id]);
  }

  get isDeadlinePassed(): boolean {
    const details = this.learningPlanInstanceDetails;
    const deadline =
      details?.trainingPhaseType === 'Custom'
        ? details.orderedPhases[0]?.endsAt
        : null;
    return !!deadline && new Date(deadline).getTime() < Date.now();
  }

  private updateComputedProperties(): void {
    if (this.learningPlanInstanceDetails) {
      const completed =
        this.learningPlanInstanceDetails.numberOfCompletedStudents;
      const enrolled =
        this.learningPlanInstanceDetails.numberOfEnrolledStudents;

      this.completionPercentage =
        enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
      this.enrolledStudentsCount = enrolled;
      this.enrolledStudentsDisplay = enrolled.toString();
      this.enrollmentBadgeType = enrolled === 0 ? 'warning' : 'transparent';
    } else {
      this.completionPercentage = 0;
      this.enrolledStudentsCount = 0;
      this.enrolledStudentsDisplay = '0';
      this.enrollmentBadgeType = 'warning';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
